import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from './api';

export interface SignedInUser {
  id: string;
  email: string;
  displayName: string | null;
  picture: string | null;
  signedInAt: string | null;
}

interface State {
  user: SignedInUser | null;
  loading: boolean;
  error: string | null;
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

export function useAuth() {
  const [state, setState] = useState<State>({ user: null, loading: true, error: null });

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const r = await apiFetch<{ user: SignedInUser | null }>('/auth/me');
      setState({ user: r.user, loading: false, error: null });
    } catch (err) {
      setState({
        user: null,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const signIn = useCallback((returnTo = '/') => {
    // /auth/* lives at the worker root (NOT under /api). Now that the worker
    // also serves the SPA, sign-in is same-origin and we just navigate to
    // the relative path. The absolute return_to keeps callback redirects
    // safe even if a future split ever puts the SPA on a different host.
    //
    // `origin` tells the callback which frontend to land back on (Studio vs
    // Throughline vs a local dev port) — the backend validates it against a
    // strict allowlist and signs it into the OAuth state; an unlisted origin
    // falls back to APP_BASE_URL server-side (this app's own origin, so this
    // call is byte-identical in practice — see apps/api/routes/auth.routes.ts).
    const absoluteReturn = new URL(returnTo, window.location.origin).toString();
    const params = new URLSearchParams({
      return_to: absoluteReturn,
      origin: window.location.origin,
    });
    window.location.href = `/auth/google?${params.toString()}`;
  }, []);

  const signOut = useCallback(async () => {
    await apiFetch('/auth/logout', { method: 'POST' });
    setState({ user: null, loading: false, error: null });
  }, []);

  return { ...state, refresh, signIn, signOut };
}
